// ---------------------------------------------------------------------------
// Canonical page-builder asset upload + image library hook
// ---------------------------------------------------------------------------
// Uses the mounted /crm/pages/assets contract instead of the retired /api/media/*
// flow so page-builder image selection, media library, and asset deletion all
// operate on the same tenant-scoped backend.

import { useState, useCallback, useRef, useEffect } from "react";
import { apiFetch } from "@/lib/api-client";

interface PageAssetResponse {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  createdAt: string;
  publicUrl?: string | null;
  publicPath?: string | null;
  fileUrl?: string | null;
}

interface AssetListResponse {
  data: {
    items: PageAssetResponse[];
    total: number;
  };
}

export interface UploadedImage {
  id: string;
  url: string;
  thumbnailUrl: string;
  width: number;
  height: number;
  size: number;
  format: string;
  alt: string;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ALLOWED_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/gif", "image/svg+xml"]);
const DEFAULT_DIMENSION = 0;

const MAGIC_BYTES: Array<{ mime: string; bytes: number[]; offset?: number }> = [
  { mime: "image/jpeg", bytes: [0xFF, 0xD8, 0xFF] },
  { mime: "image/png", bytes: [0x89, 0x50, 0x4E, 0x47] },
  { mime: "image/gif", bytes: [0x47, 0x49, 0x46, 0x38] },
  { mime: "image/webp", bytes: [0x52, 0x49, 0x46, 0x46], offset: 0 },
];

function buildAssetFilePath(assetId: string): string {
  return `/crm/pages/assets/${assetId}/file`;
}

function buildAssetAbsoluteUrl(assetId: string): string {
  const path = buildAssetFilePath(assetId);
  if (typeof window === "undefined") {
    return path;
  }
  return `${window.location.origin}${path}`;
}

function resolveAssetUrl(asset: PageAssetResponse): string {
  return asset.publicUrl || asset.publicPath || buildAssetAbsoluteUrl(asset.id);
}

function toUploadedImage(asset: PageAssetResponse): UploadedImage {
  const url = resolveAssetUrl(asset);

  return {
    id: asset.id,
    url,
    thumbnailUrl: url,
    width: asset.width ?? DEFAULT_DIMENSION,
    height: asset.height ?? DEFAULT_DIMENSION,
    size: asset.sizeBytes,
    format: asset.contentType.split("/")[1] ?? "image",
    alt: asset.filename.replace(/\.[^.]+$/, ""),
  };
}

async function verifyMagicBytes(file: File): Promise<boolean> {
  if (file.type === "image/svg+xml") {
    const text = await file.slice(0, 256).text();
    const trimmed = text.trimStart().toLowerCase();
    return trimmed.startsWith("<?xml") || trimmed.startsWith("<svg");
  }

  const buffer = await file.slice(0, 12).arrayBuffer();
  const header = new Uint8Array(buffer);

  if (file.type === "image/webp") {
    return (
      header[0] === 0x52 && header[1] === 0x49 && header[2] === 0x46 && header[3] === 0x46 &&
      header[8] === 0x57 && header[9] === 0x45 && header[10] === 0x42 && header[11] === 0x50
    );
  }

  const sig = MAGIC_BYTES.find((m) => m.mime === file.type);
  if (!sig) return false;

  const offset = sig.offset ?? 0;
  return sig.bytes.every((byte, index) => header[offset + index] === byte);
}

export function useImageUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const progressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => { if (progressTimerRef.current) clearTimeout(progressTimerRef.current); };
  }, []);

  const upload = useCallback(async (file: File): Promise<UploadedImage | null> => {
    setError(null);

    if (!ALLOWED_TYPES.has(file.type)) {
      setError("Unsupported file type. Use JPEG, PNG, WebP, GIF, or SVG.");
      return null;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError("File too large. Maximum size is 10MB.");
      return null;
    }

    const isValid = await verifyMagicBytes(file);
    if (!isValid) {
      setError("File content does not match its type. The file may be corrupted or mislabeled.");
      return null;
    }

    setIsUploading(true);
    setProgress(15);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const result = await apiFetch<{ data: PageAssetResponse }>("/crm/pages/assets", {
        method: "POST",
        body: formData,
      });

      setProgress(100);
      return toUploadedImage(result.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
      return null;
    } finally {
      setIsUploading(false);
      if (progressTimerRef.current) clearTimeout(progressTimerRef.current);
      progressTimerRef.current = setTimeout(() => setProgress(0), 150);
    }
  }, []);

  const uploadMultiple = useCallback(async (files: File[]): Promise<UploadedImage[]> => {
    const results: UploadedImage[] = [];
    for (const file of files) {
      const result = await upload(file);
      if (result) {
        results.push(result);
      }
    }
    return results;
  }, [upload]);

  return { upload, uploadMultiple, isUploading, progress, error };
}

export function useImageLibrary() {
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchImages = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiFetch<AssetListResponse>("/crm/pages/assets?limit=100");
      const nextImages = (response.data.items ?? [])
        .filter((asset) => asset.contentType.startsWith("image/"))
        .map(toUploadedImage);
      setImages(nextImages);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load uploaded images.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deleteImage = useCallback(async (imageId: string) => {
    setError(null);

    try {
      await apiFetch(`/crm/pages/assets/${imageId}`, { method: "DELETE" });
      setImages((prev) => prev.filter((image) => image.id !== imageId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete image.");
      throw err;
    }
  }, []);

  return { images, isLoading, error, fetchImages, deleteImage };
}
